/**
 * In-studio screen -> canonical `screen_*` event-name map (ENG-4324, plan SCR-1).
 *
 * This is the SINGLE SOURCE OF TRUTH open decision #7 mandates: the ticket
 * feared "two places hardcoding the mapping will drift" — historically the app's
 * screen map vs. the tests' re-hardcoded expected names. `IN_STUDIO_SCREEN_EVENTS`
 * is the one place that owns the screen -> name mapping; both the emit path
 * (analytics/screens/resolve-screen.ts -> analytics/domains/screen-lifecycle.ts)
 * and the unit tests consume THIS constant, so there is no second copy to drift.
 *
 * Scope (stay-in-scope, ai.knowledge/global): in-studio screens ONLY. These are
 * the four ROOM routes — the surfaces the generic `$pageview` collapsed into a
 * single `/room` entry, losing WHICH room. `screen_download*` (ENG-4328) and
 * `screen_agent_setup` (ENG-4343) are cross-repo and deliberately absent; the
 * account/organization/advanced settings panels and the studio-settings virtual
 * pageviews are NOT separate routes (they render on top of a room route whose
 * `screen_room` already fired with the room identity), so they are not distinct
 * in-studio route screens and are out of this map's scope.
 *
 * Keyed by the react-router route PATTERN (identical to the in-studio keys of
 * pageview/constants.ts ROUTE_MAPPINGS) so resolve-screen.ts can `matchPath`
 * against a live pathname and lift the `:roomId`. Values REFERENCE the EVENT_NAMES
 * constants rather than re-typing the string literals, so the canonical-name home
 * (event-names.ts) and this map can never disagree.
 *
 * Per rule: never-use-type-assertions (ai.knowledge/global) — `as const` object,
 * union type derived from it; no `as`, no raw screen-name literals at call sites.
 * Per rule: reference-rules-in-comments (ai.knowledge/global).
 */

import { EVENT_NAMES } from '#~/analytics/constants/event-names.js';
import { COMMON_PROPERTIES } from '#~/analytics/constants/event-properties.js';

export const IN_STUDIO_SCREEN_EVENTS = {
  '/room/:roomId': EVENT_NAMES.SCREEN_ROOM,
  '/room/:roomId/boot': EVENT_NAMES.SCREEN_ROOM_BOOT,
  '/room/:roomId/inspect': EVENT_NAMES.SCREEN_ROOM_INSPECT,
  '/room/:roomId/billing/:mode/:status?': EVENT_NAMES.SCREEN_ROOM_BILLING
} as const;

/** A react-router route pattern for one in-studio screen. */
export type InStudioScreenRoute = keyof typeof IN_STUDIO_SCREEN_EVENTS;

/** The canonical `screen_*` name union, DERIVED from the SSOT (never re-listed). */
export type ScreenEventName = (typeof IN_STUDIO_SCREEN_EVENTS)[InStudioScreenRoute];

/**
 * The canonical screen names as a flat list. Consumed by posthog/manager.ts to
 * retain the (once-per-room-entry, fires during boot) screen sends for the e2e
 * send-boundary receipt, and by tests for completeness/uniqueness checks. Typed
 * off the SSOT so a new screen automatically joins the list.
 */
export const SCREEN_EVENT_NAMES: readonly ScreenEventName[] =
  Object.values(IN_STUDIO_SCREEN_EVENTS);

/**
 * The identity property every screen event carries — the room the user is on
 * ("Product specifically wants to see which room"). Registered in each screen
 * entry's `required_properties` in canonical-event-map.json. `room_id` (not
 * `studio_id`) directly names the ticket's "which room" ask and matches the
 * canonical `room_id` prop on bridge_link_succeeded.
 *
 * Aliases the shared COMMON_PROPERTIES key rather than re-typing the literal, so
 * the emit path, this screen-domain name and the e2e receipt cannot disagree.
 */
export const SCREEN_ROOM_IDENTITY_PROPERTY = COMMON_PROPERTIES.ROOM_ID;
